/**
 * dnsLinking.js — Miroir frontend du module backend.
 *
 * MIROIR STRICT de `backend/utils/dnsLinking.js`. Toute modification ici
 * DOIT être répliquée à l'identique dans le module backend, et la cohérence
 * entre les deux est validée par `scripts/check-dns-linking.js`.
 *
 * Règle d'or : aucune dépendance externe — uniquement du JavaScript natif —
 * pour rester portable côté navigateur.
 */

export const ALLOWED_TYPES = ['AGENT', 'CONJOINT', 'CONJOINTE', 'ENFANT'];

const SEXE_F_VALUES = [
  'F',
  'FEMININ',
  'FÉMININ',
  'FEMALE',
  'FEM',
  'FEMININE',
  'FÉMININE',
  'FEMME'
];

/** @param {*} value */
export function normalizeMatriculeKey(value) {
  return String(value || '').trim().toUpperCase();
}

/** @param {*} rawSexe */
export function normalizeSexe(rawSexe) {
  const cleaned = String(rawSexe || '').trim().toUpperCase().replace(/\.$/, '');
  if (!cleaned) return '';
  if (
    cleaned === 'M' ||
    cleaned === 'MASCULIN' ||
    cleaned === 'MALE' ||
    cleaned === 'MASC' ||
    cleaned === 'HOMME'
  ) {
    return 'M';
  }
  if (SEXE_F_VALUES.includes(cleaned)) {
    return 'F';
  }
  return cleaned;
}

/**
 * @param {string} type
 * @param {string} rawSexe
 */
export function refineConjointType(type, rawSexe) {
  if (type !== 'CONJOINT' && type !== 'CONJOINTE') return type;
  return normalizeSexe(rawSexe) === 'F' ? 'CONJOINTE' : 'CONJOINT';
}

/**
 * @param {string} dependentMatricule
 * @param {string} dependentType
 */
export function extractAgentMatricule(dependentMatricule, dependentType) {
  const matricule = String(dependentMatricule || '').trim();
  const type = String(dependentType || '').trim().toUpperCase();

  if (!matricule) return '';

  if (type === 'CONJOINT' || type === 'CONJOINTE') {
    return matricule
      .replace(/CONJOINTE?$/i, '')
      .replace(/C$/i, '')
      .trim();
  }

  if (type === 'ENFANT') {
    return matricule
      .replace(/ENFANT\d*$/i, '')
      .replace(/E\d+$/i, '')
      .trim();
  }

  return matricule;
}

/**
 * @param {{ matricule?: string|null }} agent
 * @param {number} counter
 */
export function buildAgentImportKey(agent, counter) {
  const key = normalizeMatriculeKey(agent && agent.matricule);
  return key || `AGENT_ROW_${counter}`;
}

/**
 * Parcourt un tableau d'adhérents dans l'ordre du fichier et assigne à
 * chaque dépendant l'`agent_import_key` de l'agent qui le précède.
 *
 * @param {Array<object>} members - Muté en place.
 * @returns {Array<object>}
 */
export function assignAgentImportKeys(members) {
  let currentAgentKey = null;
  let agentCounter = 0;

  for (const member of members) {
    if (!member) continue;

    if (member.type === 'AGENT') {
      agentCounter += 1;
      member.agent_import_key =
        normalizeMatriculeKey(member.agent_import_key) ||
        buildAgentImportKey(member, agentCounter);
      currentAgentKey = member.agent_import_key;
    } else if (currentAgentKey) {
      member.agent_import_key =
        normalizeMatriculeKey(member.agent_import_key) || currentAgentKey;
    }
  }

  return members;
}
